import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import axios from 'axios';
import { TextInput } from '../../../../form/textInput';
import { operationType, selectDataType } from '../../../../../services/types';
import { getvaluesUsingVariables, updateNodeValue } from '../../../../../services/method';
import { Switch } from '../../../../form/switch';
import { Textarea } from '../../../../form/textarea';
import { Select } from '../../../../form/select';
import { Checkboxs } from '../../../../form/checkboxs';
import { Main, Styled } from './style';
import { getGoogleAccountToken } from '../../../../auth/method';
import { AuthenticateSelect } from '../../../../auth/select';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { ExpandedBlock } from '../../../expanded';

type GmailProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function Gmail({ id, data, setChildMinimize, setChildSettings }: GmailProps) {
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'Get attachments', refId: 'get_attachments', isActive: true }, { id: 2, name: 'Send email', refId: 'send_email' }, 
      { id: 3, name: 'Get Recent Emails', refId: 'recent_emails' }, { id: 4, name: 'Search Email Titles', refId: 'search_email_titles' }, 
      { id: 5, name: 'Search Email Content', refId: 'search_email_content' },
      { id: 6, name: 'Human in the Loop', refId: 'human_in_the_loop' },
      { id: 7, name: 'Let AI Agent Decide', refId: 'agent_decide' }
   ]);
   const [types, setTypes] = React.useState<selectDataType[]>([
      { id: 1, name: 'Fetch all emails', refId: 'all_emails', isActive: true }, { id: 2, name: 'Fetch new emails', refId: 'new_emails' },
   ]);
   const { getNodes, getEdges, setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const hitlModes: selectDataType[] = [
      { id: 1, name: 'Decision only (recommended)', refId: 'decision_only', isActive: (data.value?.hitl_mode || 'decision_only') === 'decision_only' },
      { id: 2, name: 'Append reply as context', refId: 'append_context', isActive: data.value?.hitl_mode === 'append_context' },
      { id: 3, name: 'Use reply as prompt', refId: 'replace_prompt', isActive: data.value?.hitl_mode === 'replace_prompt' },
   ];

   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) {
         if (data.currentCategory === 'Actions') {
            setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === 'send_email' })));
            setNodes(nodes => nodes.map(node => {
               if (node.id === id) {
                  return { ...node, data: { ...node.data, value: { ...node.data.value, request: 'send_email', isMultiRecipient: false, message_recipients_json: '' }}}
               } else return node;
            }));
         }
         else updateNodeValue(id, setNodes, { request: requests[0].refId, fetchType: types[0].refId });
      } else {
         setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === data.value.request })));
         setTypes(types => types.map(type => ({ ...type, isActive: type.refId === data.value.fetchType })));
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const handleAuthenticate = async (type: 'add' | 'remove', data: any) => {
      if (type === 'add') { 
         // scope="https://www.googleapis.com/auth/gmail.compose https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.modify"
         updateNodeValue(id, setNodes, { connection: data, connectionId: data?.id || data?._id || null });
      } else if (type === 'remove') {
         updateNodeValue(id, setNodes, { connection: null });
      }
   }

   // ---
   const handleSelectRequests = (action: 'request' | 'type', selectId: number) => {
      if (action === 'request') {
         const refId = requests.find(obj => obj.id === selectId)?.refId;

         setRequests((requests) => requests.map((request) => {
            if (request.id === selectId) return { ...request, isActive: true };
            else return { ...request, isActive: false };
         }));
         setNodes(nodes => nodes.map(node => {
            if (node.id === id) {
               return { ...node, data: { ...node.data, value: { ...node.data.value, request: refId }}}
            } else return node;
         }));

      } else if (action === 'type') {
         setTypes((types) => types.map((type) => {
            if (type.id === selectId) return { ...type, isActive: true };
            else return { ...type, isActive: false };
         }));
         updateNodeValue(id, setNodes, { fetchType: types.find(obj => obj.id === selectId)?.refId });
      }
   }
   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      const value = e.target.value;
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: value });
   }

   // ---
   const hanedleFileAction = async (type: 'fetch' | 'download') => {
      if (type === 'fetch') {
         try {
            updateNodeValue(id, setNodes, { isLoading: true });
            const edges = getEdges();
            const nodes = getNodes();
            const keywordsTargets: operationType[] = [];
            let general = { keywords: data.value.keywords || '', max_results: data.value.max_results || 1 };
            // Variables system
            general = getvaluesUsingVariables(general, nodes) as any;

            for (const edge of edges) {
               if (edge.target === id) {
                  for (const node of nodes) {
                     if (node.id === edge.source && edge.targetHandle === 'Keywords') keywordsTargets.push({ id: node.id, ...node.data });
                  }
               }
            }
            for (const item of keywordsTargets) {
               if (item.action === 'input' && item.value) general.keywords += ` ${item.value}`;
               else if (item.outputType === 'text' && item.executeRun) general.keywords += `${await item.executeRun({ id: item.id as string, nodes, setNodes, edges, isNeedValue: true, blockIdNeedValue: id })} `;
            }
            
            const token = await getGoogleAccountToken(data.value?.connection, id, setNodes);
            const response = (await axios.get(
               `/actions/gmail/${data.value.fetchType === 'all_emails' ? 'watch_emails' : 'fetch_new_emails_with_attachments'}?token=${token}&keywords=${general.keywords}&max_results=${general.max_results}`
            )).data;
            
            const files = [];
            for (const item of response) files.push(...item.attachments);
            updateNodeValue(id, setNodes, { isLoading: false, fileId: '', files: files.map((file, index) => ({ id: index+1, name: file.filename, url: file.download_url, type: file.mime_type })) });

         } catch {
            updateNodeValue(id, setNodes, { isLoading: false });
         }
      } else if (type === 'download') {
         const file = data.value.files.find((file: any) => file.isActive);
         window.open(file.url, '_blank')
      }
   }

   // ---
   const filesLengthActive = data.value?.files?.filter((item: any) => item.isActive)?.length;
   const isWarning = !data.value?.connection;

   return (<>
      <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={ 21 } /></span>
         </> : null}
         { isExpanded ? 
         <ExpandedBlock 
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            style={{ minWidth: '420px' }}
         >
            <Styled>
               <AuthenticateSelect type="google" defaultId={ data.value?.connectionId || data.value?.connection?._id || data.value?.connection?.id || '' } defaultConnection={ data.value?.connection } handleAuthenticate={ handleAuthenticate } background={ data.background } />
               <div className="action">
                  { data.value?.request === 'get_attachments' && data.value?.connection ? <>
                     <button className="btn1 withBorder" onClick={ _=> hanedleFileAction('fetch') } disabled={ data.value?.isLoading }>Fetch</button> 
                     { filesLengthActive === 1 ? <button className="btn1 withBorder download" onClick={ _=> hanedleFileAction('download') }>Download</button> : null }
                  </> : null }
               </div>
               <Select title="Request" data={ requests } handleSelectItem={ (id) => handleSelectRequests('request', id) } isSmall={ true } />
               { ['send_email', 'human_in_the_loop'].includes(data.value?.request) ? <>
                  { data.value?.request === 'send_email' && data.value?.isMultiRecipient ? (
                     <Textarea
                        // title="Recipients (JSON)"
                        keyRef="message_recipients_json"
                        value={ data.value?.message_recipients_json }
                        placeholder={
`[{ "email": "example1@gmail.com", "name": "name one" },{ "email": "example2@gmail.com" }]`
                        }
                        handleChange={ (e) => updateNodeValue(id, setNodes, { message_recipients_json: e.target.value }) }
                     />
                  ) : (
                     <TextInput keyRef="message_recipient" value={ data.value?.message_recipient } title="Recipient" placeholder="message_recipient" handleChange={ handleChangeInput } />
                  )}
                  <TextInput keyRef="message_subject" value={ data.value?.message_subject } title="Subject" placeholder="message_subject" handleChange={ handleChangeInput } />
                   { data.value?.request === 'send_email' && data.value?.isMultiRecipient ? (
                     <Textarea 
                     value={ data.value?.message_content } 
                     placeholder={`Hi {{name}},

message_content`}
                     handleChange={ (e) => updateNodeValue(id, setNodes, { message_content: e.target.value }) } 
                  /> 
                  ) : (
                  <Textarea 
                     value={ data.value?.message_content } 

                     placeholder="message_content" 
                     handleChange={ (e) => updateNodeValue(id, setNodes, { message_content: e.target.value }) } 
                  />   )}
                 
                  { data.value?.request === 'send_email' ? <>
                     <Switch title="Create Draft" value={ !!data.value?.isDraft } setValue={ () => updateNodeValue(id, setNodes, { isDraft: !data.value?.isDraft }) } />
                     <Switch title="Multi Recipients (JSON)" value={ !!data.value?.isMultiRecipient } setValue={ () => updateNodeValue(id, setNodes, { isMultiRecipient: !data.value?.isMultiRecipient }) } />
                     <div className="status">
                        <div>
                           <span className={ data.value?.doneSent ? 'green' : ''}></span>
                           <span>Message status</span> 
                        </div>
                        <span>{ data.value?.doneSent ? '✔ Sent' : 'N/A'}</span>
                     </div>
                  </> : <div style={{ marginTop: '10px' }}>
                     <Select title="HITL" data={ hitlModes } handleSelectItem={ (selectId) => updateNodeValue(id, setNodes, { hitl_mode: hitlModes.find(item => item.id === selectId)?.refId || 'decision_only' }) } isSmall={ true } />
                     <TextInput keyRef="hitl_wait_seconds" value={ data.value?.hitl_wait_seconds } title="Timeout" placeholder="600 (sec)" handleChange={ handleChangeInput } />
                     <div className="hitlHint">Reply with APPROVE or REJECT in the first line. Poll interval and reply sender filter use safe defaults automatically.</div>
                  </div> }
               </> : data.value?.request === 'get_attachments' ? <>
                  <Select title="Type" data={ types } handleSelectItem={ (id) => handleSelectRequests('type', id) } isSmall={ true } />
                  { data.value?.files?.length ? <div className="filesEdit">
                     <h3 className="title" style={{ backgroundColor: data.background, color: '#fff' }}>Attachments ({ filesLengthActive }/{ data.value?.files?.length })</h3>
                     <div className="scroll nowheel">
                        <Checkboxs 
                           data={ data.value.files } 
                           handelCheckbox={ (fileId: any, value?: boolean) => { 
                              updateNodeValue(id, setNodes, { files: data.value.files.map((item: any) => {
                                 if (item.id === fileId) return ({ ...item, isActive: !value })
                                 else return item;
                              })});
                           }} 
                        />
                     </div>
                  </div> : null }
                  <TextInput keyRef="keywords" value={ data.value?.keywords } title="Keywords" placeholder="keywords" handleChange={ handleChangeInput } />
                  <TextInput keyRef="max_results" value={ data.value?.max_results } title="Limit" placeholder="max_results" handleChange={ handleChangeInput } />
                  <Switch title="Start autopilot (with loop)" className="autopilot" value={ !!data.value?.isAutopilot } setValue={ () => updateNodeValue(id, setNodes, { isAutopilot: !data.value?.isAutopilot }) } />
                  <p>Please select your keywords carefully to avoid downloading incorrect or unintended files!</p>
               </> : <>
                  <TextInput keyRef="keywords" value={ data.value?.keywords } title="Keywords" placeholder="keywords" handleChange={ handleChangeInput } />
                  <TextInput keyRef="max_results" value={ data.value?.max_results } title="Limit" placeholder="max_results" handleChange={ handleChangeInput } />
               </>}
               { data.value?.request === 'recent_emails' ? <div style={{ margin: '20px 0 5px' }}>
                  <Switch title="Include Spam" value={ !!data.value?.isSpam } setValue={ () => updateNodeValue(id, setNodes, { isSpam: !data.value?.isSpam }) } />
               </div> : null }
            </Styled>
         </ExpandedBlock> : null}
      </Main>
   </>);
}
